import { Link } from "react-router-dom";
import { Mail, ArrowLeft, MessageCircle, CreditCard, Bug, Sparkles, Clock } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import Masthead from "@/components/Masthead";

/**
 * ContactPage (/contact)
 *
 * Public contact page — visible in the footer and sitemap. Required by
 * payment providers (Stripe, Polar, Lemon Squeezy) for account activation
 * and by consumer-protection regulations (GDPR, UAE consumer law) so
 * customers can reach a human without hunting.
 *
 * All CTAs are `mailto:` links to `support@timecapsule24.tv` with
 * prefilled subject lines so tickets are easier to triage.
 */
const SUPPORT_EMAIL = "support@timecapsule24.tv";

function mailto(subject) {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

const TOPICS = [
  {
    key: "billing",
    icon: <CreditCard size={18} />,
    title: "Billing & Subscriptions",
    body: "Questions about payments, subscription changes, refunds, or invoices.",
    subject: "TimeCapsule 24 — Billing question",
  },
  {
    key: "bug",
    icon: <Bug size={18} />,
    title: "Report a Bug",
    body: "Something isn't working right — playback, region data, TV pairing, or anything else.",
    subject: "TimeCapsule 24 — Bug report",
  },
  {
    key: "feature",
    icon: <Sparkles size={18} />,
    title: "Feature Requests",
    body: "Ideas for what to build next — dates, regions, formats, integrations.",
    subject: "TimeCapsule 24 — Feature idea",
  },
  {
    key: "general",
    icon: <MessageCircle size={18} />,
    title: "Anything Else",
    body: "Press, partnerships, licensing, or just to say hi.",
    subject: "TimeCapsule 24 — Hello",
  },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen w-full bg-paper text-ink paper-grain">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Back link */}
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] sm:text-xs border-2 border-ink px-3 py-1.5 hover:bg-ink hover:text-paper"
            data-testid="contact-back-btn"
          >
            <ArrowLeft size={12} /> Home
          </Link>
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-ink-muted hidden sm:block">
            TimeCapsule 24 · Contact
          </div>
        </div>

        <Masthead volume="I" issueLabel="Contact" bylineTagline="Talk to the humans behind TimeCapsule 24" />

        {/* Hero */}
        <section className="mt-8 border-2 border-ink bg-paper-alt p-6 sm:p-10" data-testid="contact-hero">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-press-red mb-2">
            ✎ How to reach us
          </div>
          <h1 className="font-headline uppercase font-black leading-[0.9] tracking-tighter text-4xl sm:text-6xl">
            Get in touch.
          </h1>
          <p className="mt-3 font-body italic text-base sm:text-lg text-ink-muted max-w-2xl">
            One inbox, watched by a real person (usually me). Whether it&rsquo;s a billing question, a bug, or a whimsical idea for the next iconic date — every message gets read.
          </p>

          <a
            href={mailto("TimeCapsule 24 — Support request")}
            onClick={() => trackEvent("contact_email_click", { source: "hero" })}
            data-testid="contact-email-btn"
            className="mt-6 inline-flex items-center gap-2 bg-ink text-paper border-2 border-ink px-4 py-3 font-mono uppercase tracking-widest text-sm sm:text-base shadow-hard-sm hover:bg-press-red"
            style={{ transition: "background-color 150ms" }}
          >
            <Mail size={16} />
            {SUPPORT_EMAIL}
          </a>

          <div className="mt-4 flex items-center gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-ink-muted">
            <Clock size={12} />
            <span>Typical response · within 24 hours (Mon–Fri, GMT+4 Dubai)</span>
          </div>
        </section>

        {/* Topic cards */}
        <section className="mt-8">
          <h2 className="font-mono uppercase tracking-[0.3em] text-xs sm:text-sm text-ink-muted mb-4">
            — Pick a topic (helps us route faster) —
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t-2 border-l-2 border-ink" data-testid="contact-topics">
            {TOPICS.map((t) => (
              <a
                key={t.key}
                href={mailto(t.subject)}
                onClick={() => trackEvent("contact_email_click", { source: "topic", topic: t.key })}
                data-testid={`contact-topic-${t.key}`}
                className="group border-r-2 border-b-2 border-ink p-5 sm:p-6 bg-paper hover:bg-paper-alt"
                style={{ transition: "background-color 150ms" }}
              >
                <div className="flex items-center gap-2 mb-2 text-press-red">
                  {t.icon}
                  <div className="font-mono uppercase tracking-[0.2em] text-[10px] sm:text-xs">
                    {t.title}
                  </div>
                </div>
                <div className="font-body text-sm sm:text-base text-ink leading-snug">
                  {t.body}
                </div>
                <div className="mt-3 font-mono uppercase tracking-widest text-[10px] text-ink-muted group-hover:text-press-red">
                  ✉ Email us →
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Micro-FAQ */}
        <section className="mt-8 border-2 border-ink p-6 sm:p-8 bg-paper-surface">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-ink-muted mb-3">
            — Quick answers before you write —
          </div>
          <dl className="space-y-4">
            <div>
              <dt className="font-headline uppercase font-bold text-sm sm:text-base tracking-tight">
                How do I cancel my subscription?
              </dt>
              <dd className="mt-1 font-body italic text-sm text-ink-muted">
                Sign in → Profile menu → &ldquo;Manage subscription&rdquo; → opens Stripe&rsquo;s customer portal. Cancel any time; access lasts until end of the current billing period.
              </dd>
            </div>
            <div>
              <dt className="font-headline uppercase font-bold text-sm sm:text-base tracking-tight">
                Can I get a refund?
              </dt>
              <dd className="mt-1 font-body italic text-sm text-ink-muted">
                Yes, if within 14 days of your first charge. Email us with your subscription email — we&rsquo;ll process it same-day.
              </dd>
            </div>
            <div>
              <dt className="font-headline uppercase font-bold text-sm sm:text-base tracking-tight">
                Does TimeCapsule work on my TV?
              </dt>
              <dd className="mt-1 font-body italic text-sm text-ink-muted">
                Yes — head to <Link to="/tv" className="underline hover:text-press-red">/tv</Link> for the setup guide, or open <Link to="/tv/cast" className="underline hover:text-press-red">/tv/cast</Link> on your TV and pair your phone with a 4-digit code.
              </dd>
            </div>
            <div>
              <dt className="font-headline uppercase font-bold text-sm sm:text-base tracking-tight">
                Data & privacy?
              </dt>
              <dd className="mt-1 font-body italic text-sm text-ink-muted">
                We store your login email, subscription status, and favourite dates. Nothing sold, nothing shared. Ask us and we&rsquo;ll delete everything.
              </dd>
            </div>
          </dl>
        </section>

        <footer className="mt-10 pt-6 border-t-2 border-ink flex flex-col sm:flex-row justify-between gap-2 font-mono text-xs uppercase tracking-widest text-ink-muted">
          <span>TimeCapsule 24 · A daily portal to the past</span>
          <span>Made with care in Dubai, GMT+4</span>
        </footer>
      </div>
    </div>
  );
}
